import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Icon, Modal, ModalHeader } from 'common/components';
import { RootState } from 'common/redux/core/root.reducer';
import { getEntityDetailsResponse } from 'common/redux/entity/entity.selector';

import { ApiStatus, EntityActions, EntityType, ModalSize } from 'common/enums';
import { entityActions } from 'common/redux/entity';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';

import { ICON_COLLECTION } from 'assets/media/icons';

import './EntityDetailsPopup.scss';
import { getisEditActionAccess } from 'modules/master-data/utils/master-data.utils';
import useMasterDataAccess from 'modules/master-data/role-access-hooks/useMasterDataAccess';

type EntityDetailsPopupType = {
    entity: EntityDetailsPayload;
    isShowDetailsPopup: boolean;
    isEditHidden?: boolean;
    handleEditClick?: (data: EntityDetailsPayload) => void;
    closeHandler: () => void;
};
export default function EntityDetailsPopup({
    entity,
    isShowDetailsPopup,
    isEditHidden,
    handleEditClick,
    closeHandler
}: EntityDetailsPopupType) {
    const dispatch = useDispatch();

    const entityDetailsResponse = useSelector((state: RootState) =>
        getEntityDetailsResponse(state)
    );

    useEffect(() => {
        if (entity && entity.id.length > 0) dispatch(entityActions.fetchEntityDetails(entity));
    }, [dispatch, entity]);

    const headerElement = (
        <header className="details-header-name-wrapper">
            Details:{' '}
            <span className="details-header-name" title={entityDetailsResponse?.data?.name}>
                {entityDetailsResponse?.data?.name ? entityDetailsResponse?.data?.name : ''}
            </span>
        </header>
    );

    return (
        <Modal
            show={isShowDetailsPopup && entityDetailsResponse?.status === ApiStatus.SUCCESS}
            onClose={() => closeHandler()}
            size={ModalSize.XLarge}>
            <ModalHeader>{headerElement}</ModalHeader>
            <div className="details-edit">
                <span className="detail-info">
                    Below are details for ‘
                    <span className="details-body-name" title={entityDetailsResponse?.data?.name}>
                        {entityDetailsResponse?.data?.name ? entityDetailsResponse?.data?.name : ''}
                    </span>
                    ’{isEditHidden === true ? ':' : '. Click on the edit icon to make changes:'}
                </span>

                {isEditHidden !== true && (
                    <Icon
                        className={
                            entity.isEditActionDisabled || entity.isChildLink
                                ? 'access-icon-disabled'
                                : 'access-icon'
                        }
                        src={ICON_COLLECTION.IC_Edit}
                        onClick={() =>
                            entity.isEditActionDisabled || entity.isChildLink
                                ? ''
                                : handleEditClick?.(entity)
                        }
                    />
                )}
            </div>
            {entityDetailsResponse?.data?.editedUser && (
                <div className="details-wrapper">
                    <span className="title">Editor user</span>
                    <span className="detail">{entityDetailsResponse?.data?.editedUser}</span>
                </div>
            )}
            {entityDetailsResponse?.data?.lastModified && (
                <div className="details-wrapper">
                    <span className="title">Last modified</span>
                    <span className="detail">
                        {new Date(entityDetailsResponse?.data?.lastModified)
                            .toLocaleDateString('en-GB', { dateStyle: 'medium' })
                            .replaceAll(' ', '-') +
                            ', ' +
                            new Date(entityDetailsResponse?.data?.lastModified)
                                .toLocaleTimeString('en-GB', {
                                    timeStyle: 'short',
                                    hourCycle: 'h12'
                                })
                                .toUpperCase() +
                            ' (UTC)'}
                    </span>
                </div>
            )}
            {entityDetailsResponse?.data?.dateAdded && (
                <div className="details-wrapper">
                    <span className="title">Date added</span>
                    <span className="detail">
                        {new Date(entityDetailsResponse?.data?.dateAdded)
                            .toLocaleDateString('en-GB', { dateStyle: 'medium' })
                            .replaceAll(' ', '-') +
                            ', ' +
                            new Date(entityDetailsResponse?.data?.dateAdded)
                                .toLocaleTimeString('en-GB', {
                                    timeStyle: 'short',
                                    hourCycle: 'h12'
                                })
                                .toUpperCase() +
                            ' (UTC)'}
                    </span>
                </div>
            )}
            {entityDetailsResponse?.data?.mappedTo &&
                entityDetailsResponse?.data?.mappedTo?.length > 0 && (
                    <div className="details-wrapper">
                        <span className="title">Mapped to</span>
                        <div className="mapped-wrapper">
                            {entityDetailsResponse?.data?.mappedTo?.map(
                                (sub: string, index: number) => (
                                    <span key={index} className="mapped-to">
                                        {sub}
                                    </span>
                                )
                            )}
                        </div>
                    </div>
                )}
        </Modal>
    );
}
